import { type FormikErrors } from 'formik'
import { isEqual, pickBy } from 'lodash'
import { type IntlShape } from 'react-intl'
import * as Yup from 'yup'

import { type Currency } from '@webapp/core/entities/external/Currency'
import {
  ExternalBankAccountTypes,
  type ExternalBankAccountTypeEnum,
} from '@webapp/platform/banking/entities/enums/ExternalBankAccountTypeEnum'
import messages from './messages'

export interface ExternalBankAccountTypeOption {
  value: ExternalBankAccountTypeEnum
  label: string
}

// Select-backed fields store the full option object; id extraction happens
// at the presenter submit boundary.
export interface ExternalBankAccountFormSchema {
  name: string
  type: ExternalBankAccountTypeOption | null
  currency: Currency | null
  description: string
  startNumber: string
  bankName: string
  bankRoutingNumber: string
  companyName: string
  companyRoutingNumber: string
  companyTaxId: string
  glCode: string
  createAnother: boolean
}

export type ExternalBankAccountFormField = Exclude<
  keyof ExternalBankAccountFormSchema,
  'createAnother'
>

export const buildExternalBankAccountFormInitialValues = (
  typeOptions: ExternalBankAccountTypeOption[],
  restrictTypeToOther: boolean
): ExternalBankAccountFormSchema => ({
  name: '',
  // restrictTypeToOther forces the type to Other and hides the select
  // (BHV-013).
  type: restrictTypeToOther
    ? (typeOptions.find(
        (option) => option.value === ExternalBankAccountTypes.OTHER
      ) ?? null)
    : null,
  currency: null,
  description: '',
  startNumber: '',
  bankName: '',
  bankRoutingNumber: '',
  companyName: '',
  companyRoutingNumber: '',
  companyTaxId: '',
  glCode: '',
  createAnother: false,
})

const isType =
  (expected: ExternalBankAccountTypeEnum) =>
  (type: ExternalBankAccountTypeOption | null | undefined) =>
    type?.value === expected

const TYPE_FIELDS_BY_TYPE: Partial<
  Record<ExternalBankAccountTypeEnum, ExternalBankAccountFormField[]>
> = {
  [ExternalBankAccountTypes.OTHER]: ['description', 'glCode'],
  [ExternalBankAccountTypes.CHECK]: ['startNumber', 'glCode'],
  [ExternalBankAccountTypes.ACH]: [
    'bankName',
    'bankRoutingNumber',
    'companyName',
    'companyRoutingNumber',
    'companyTaxId',
    'glCode',
  ],
}

// The fields the form renders for its current type (BHV-012/BHV-013), the
// same split the required-by-type rules below validate.
export const getRenderedExternalBankAccountFormFields = (
  type: ExternalBankAccountTypeOption | null,
  restrictTypeToOther: boolean
): ExternalBankAccountFormField[] => [
  'name',
  ...(restrictTypeToOther ? [] : (['type'] as const)),
  'currency',
  ...((type && TYPE_FIELDS_BY_TYPE[type.value]) ?? []),
]

/**
 * Formik `status` after a failed save, set by the submitting presenter: the
 * server's field errors and the values the server rejected.
 */
export interface ExternalBankAccountFormStatus {
  serverErrors: Partial<Record<ExternalBankAccountFormField, string>>
  rejectedValues: ExternalBankAccountFormSchema
}

// The server errors on rendered fields that still hold the value the server
// rejected. Editing a field drops its own server error and no other; a field
// a type change hides takes its error with it.
export const getStandingServerErrors = (
  values: ExternalBankAccountFormSchema,
  status: ExternalBankAccountFormStatus | undefined,
  restrictTypeToOther: boolean
): FormikErrors<ExternalBankAccountFormSchema> => {
  const renderedFields = getRenderedExternalBankAccountFormFields(
    values.type,
    restrictTypeToOther
  )

  return pickBy(status?.serverErrors, (_message, key) => {
    const field = key as ExternalBankAccountFormField

    return (
      renderedFields.includes(field) &&
      isEqual(values[field], status?.rejectedValues[field])
    )
  })
}

// Required-by-type rules: Other → description; Check → start number;
// ACH → all five bank/company fields (BHV-012/BHV-014).
export const buildExternalBankAccountFormValidationSchema = (intl: IntlShape) =>
  Yup.object({
    name: Yup.string().required(intl.formatMessage(messages.nameRequiredError)),
    type: Yup.object()
      .nullable()
      .required(intl.formatMessage(messages.typeRequiredError)),
    currency: Yup.object()
      .nullable()
      .required(intl.formatMessage(messages.currencyRequiredError)),
    description: Yup.string().when('type', {
      is: isType(ExternalBankAccountTypes.OTHER),
      then: Yup.string().required(
        intl.formatMessage(messages.descriptionRequiredError)
      ),
    }),
    startNumber: Yup.string().when('type', {
      is: isType(ExternalBankAccountTypes.CHECK),
      then: Yup.string().required(
        intl.formatMessage(messages.startNumberRequiredError)
      ),
    }),
    bankName: Yup.string().when('type', {
      is: isType(ExternalBankAccountTypes.ACH),
      then: Yup.string().required(
        intl.formatMessage(messages.bankNameRequiredError)
      ),
    }),
    bankRoutingNumber: Yup.string().when('type', {
      is: isType(ExternalBankAccountTypes.ACH),
      then: Yup.string().required(
        intl.formatMessage(messages.bankRoutingNumberRequiredError)
      ),
    }),
    companyName: Yup.string().when('type', {
      is: isType(ExternalBankAccountTypes.ACH),
      then: Yup.string().required(
        intl.formatMessage(messages.companyNameRequiredError)
      ),
    }),
    companyRoutingNumber: Yup.string().when('type', {
      is: isType(ExternalBankAccountTypes.ACH),
      then: Yup.string().required(
        intl.formatMessage(messages.companyRoutingNumberRequiredError)
      ),
    }),
    companyTaxId: Yup.string().when('type', {
      is: isType(ExternalBankAccountTypes.ACH),
      then: Yup.string().required(
        intl.formatMessage(messages.companyTaxIdRequiredError)
      ),
    }),
  })
